import { useEffect, useRef, useState } from "react"
import { format } from "date-fns"
import { toast } from "react-toastify"
import { CallDataSourceDto } from "@dialogics/shared/dto/types"
import { IngestionStatusName } from "@dialogics/shared/dto/temporal"
import { useAppDispatch } from "@/app/hooks"
import { dataSourcesApi, useIngestionStatusQuery, useStartIngestionMutation } from "./service"
import {
  getDefaultIngestionDate,
  INGESTION_STATUS_VIEW,
  isValidIngestionDate,
  toIngestionDateFrom,
} from "./ingestion-utils"

const POLLING_INTERVAL_MS = 3000
const DATE_TIME_FORMAT = "dd.MM.yyyy HH:mm:ss"

const formatDateTime = (value?: string) => (value ? format(new Date(value), DATE_TIME_FORMAT) : "—")

const getErrorMessage = (e: unknown) =>
  (e as { data?: { message?: string } })?.data?.message ?? "Ошибка при запуске загрузки"

export function RunIngestionModal({ dataSource, onClose }: { dataSource: CallDataSourceDto; onClose: () => void }) {
  const dispatch = useAppDispatch()
  const [dateFrom, setDateFrom] = useState(() => getDefaultIngestionDate(dataSource.vars, new Date()))
  const [pollingInterval, setPollingInterval] = useState(0)
  const { data: run, isLoading } = useIngestionStatusQuery(dataSource.id, { pollingInterval })
  const [startIngestion, { isLoading: isStarting }] = useStartIngestionMutation()

  const status = run?.status
  const isRunning = status === "RUNNING"
  const view = status ? INGESTION_STATUS_VIEW[status] : undefined
  const today = format(new Date(), "yyyy-MM-dd")
  const canStart = !isRunning && !isStarting && !isLoading && isValidIngestionDate(dateFrom, new Date())

  // Poll only while the run is in progress
  useEffect(() => {
    setPollingInterval(isRunning ? POLLING_INTERVAL_MS : 0)
  }, [isRunning])

  // Notify only on a RUNNING → final transition, not for a historical result shown on open
  const previousStatus = useRef<IngestionStatusName | undefined>(undefined)
  useEffect(() => {
    if (previousStatus.current === "RUNNING" && status && status !== "RUNNING") {
      if (status === "COMPLETED") {
        toast.success("Загрузка звонков завершена")
      } else {
        toast.error(`Загрузка звонков: ${view?.label ?? status}${run?.error ? ` — ${run.error}` : ""}`)
      }
      // lastScanDate has changed — refresh the data sources list
      dispatch(dataSourcesApi.util.invalidateTags(["CallDataSource"]))
    }
    previousStatus.current = status
  }, [status, view, run?.error, dispatch])

  const handleStart = async () => {
    const iso = toIngestionDateFrom(dateFrom)
    if (!iso) return
    try {
      await startIngestion({ id: dataSource.id, dateFrom: iso }).unwrap()
      toast.info("Загрузка звонков запущена")
    } catch (e) {
      toast.error(getErrorMessage(e))
    }
  }

  return (
    <dialog className="modal" open>
      <div className="modal-box w-11/12 max-w-lg">
        <h2 className="text-xl font-bold">Загрузка звонков</h2>
        <p className="text-sm opacity-70 mt-1">
          Источник #{dataSource.id} · {dataSource.type}
        </p>

        <fieldset className="fieldset mt-4">
          <legend className="fieldset-legend">Дата начала</legend>
          <input
            type="date"
            className="input w-full"
            value={dateFrom}
            max={today}
            onChange={e => setDateFrom(e.target.value)}
            disabled={isRunning}
          />
          <p className="label">
            {dataSource.vars?.lastScanDate
              ? `Последняя загрузка: ${formatDateTime(dataSource.vars.lastScanDate)}`
              : "Загрузка ещё не выполнялась — по умолчанию за последний месяц"}
          </p>
        </fieldset>

        {run && (
          <fieldset className="fieldset mt-2">
            <legend className="fieldset-legend">Последний запуск</legend>
            <div className="flex items-center gap-2">
              <span className={`badge ${view?.badge ?? "badge-ghost"}`} data-testid="ingestion-status">
                {isRunning && <span className="loading loading-spinner loading-xs" />}
                {view?.label ?? status}
              </span>
            </div>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm mt-2">
              {run.dateFrom && (
                <>
                  <dt className="opacity-70">Дата начала</dt>
                  <dd>{format(new Date(run.dateFrom), "dd.MM.yyyy")}</dd>
                </>
              )}
              <dt className="opacity-70">Запущено</dt>
              <dd>{formatDateTime(run.startTime)}</dd>
              {run.closeTime && (
                <>
                  <dt className="opacity-70">Завершено</dt>
                  <dd>{formatDateTime(run.closeTime)}</dd>
                </>
              )}
            </dl>
            {run.error && <div className="alert alert-error alert-soft text-sm mt-2">{run.error}</div>}
          </fieldset>
        )}

        <div className="modal-action">
          <button type="button" className="btn" onClick={onClose}>
            Закрыть
          </button>
          <button type="button" className="btn btn-primary" onClick={handleStart} disabled={!canStart}>
            {isStarting && <span className="loading loading-spinner loading-sm" />}
            Запустить
          </button>
        </div>
      </div>
      <form method="dialog" className="modal-backdrop" onClick={onClose}>
        <button>закрыть</button>
      </form>
    </dialog>
  )
}
